<!--
  Nuovo cliente — sul modello della pagina dell'admin (`AddCustomerPage`).

  Cosa cambia rispetto a prima:
  · il tipo (privato / azienda) si sceglie con due riquadri;
  · l'email si controlla appena compilata: se il cliente e' gia' in Simba lo
    si aggiunge ai propri clienti, invece di scoprirlo al salvataggio con un
    «mail gia' usata»;
  · l'esito dell'indirizzo si vede. Al salvataggio il backend lo cerca su
    Google Maps e, se non lo trova, il cliente NON viene creato;
  · il pacchetto valet si propone dentro l'auto a cui appartiene, con il
    modello nuovo (valet all'anno, durata, prezzo a rate), e solo il
    venditore puo' farlo;
  · dopo il salvataggio si torna da dove si e' partiti: Clienti oppure la
    nuova prenotazione, con il cliente gia' in tendina.

  ⚠️ I vecchi FormCustomerComponent / FormCarComponent / FormComponent non si
  toccano: li usano ancora la scheda cliente e la pagina di aggiunta auto.
-->
<template>
  <div class="ncl">
    <header class="ncl__head">
      <button
        type="button"
        class="ncl__back"
        :aria-label="ritornoPrenotazione ? 'Torna alla prenotazione' : 'Torna a Clienti'"
        @click="annulla"
      >
        ‹
      </button>
      <div>
        <h1 class="ncl__title">
          Nuovo cliente
        </h1>
        <p class="ncl__sub">
          {{ sottotitolo }}
        </p>
      </div>
    </header>

    <div
      v-if="contesto.stato === 'errore'"
      class="ncl__banner ncl__banner--danger"
      role="alert"
    >
      <span
        class="ncl__bannerIcon"
        aria-hidden="true"
      >!</span>
      <div class="ncl__bannerBody">
        Non riesco a leggere i dati del tuo partner, e senza non posso collegargli il cliente.
        <div class="ncl__bannerActions">
          <el-button
            size="small"
            @click="caricaContesto"
          >
            Riprova
          </el-button>
        </div>
      </div>
    </div>

    <div class="fx ncl__stack">
      <!-- ---------- tipo ---------- -->
      <section class="form-card">
        <div class="form-card__head">
          <h3>Tipo di cliente</h3>
        </div>
        <div class="ncl__body">
          <div
            class="ncl__types"
            role="radiogroup"
            aria-label="Tipo di cliente"
          >
            <button
              v-for="t in TIPI"
              :key="t.value"
              type="button"
              role="radio"
              class="ncl__type"
              :class="{ 'is-on': tipo === t.value }"
              :aria-checked="tipo === t.value"
              @click="tipo = t.value"
            >
              <span
                class="ncl__typeIcon"
                aria-hidden="true"
              >
                <el-icon :size="18"><component :is="t.icona" /></el-icon>
              </span>
              <span>
                <span class="ncl__typeName">{{ t.nome }}</span>
                <span class="ncl__typeHint">{{ t.hint }}</span>
              </span>
            </button>
          </div>
        </div>
      </section>

      <!-- ---------- anagrafica ---------- -->
      <section class="form-card">
        <div class="form-card__head">
          <h3>{{ isAzienda ? 'Dati azienda e referente' : 'Dati anagrafici' }}</h3>
        </div>
        <div class="ncl__body">
          <!-- l'email per prima: decide se il resto va compilato -->
          <div class="ncl__grid">
            <div class="ncl__f ncl__f--2">
              <label
                class="campo-label"
                for="ncl-mail"
              >Email <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-mail"
                ref="campoEmail"
                v-model="cliente.mail"
                autocomplete="off"
                placeholder="Serve al cliente per accedere all'app"
                @blur="controllaEmail"
              />
              <span
                v-if="email.stato === 'verifica'"
                class="ncl__help"
              >Controllo se è già registrata…</span>
              <span
                v-else-if="email.stato === 'libera'"
                class="ncl__help ncl__help--ok"
              >✓ Nessun cliente Simba usa questa email</span>
              <span
                v-else-if="email.stato === 'errore'"
                class="ncl__help"
              >Non sono riuscito a controllarla: se è già registrata, te lo dirò al salvataggio.</span>
            </div>
          </div>

          <div
            v-if="email.stato === 'esiste'"
            class="ncl__banner"
            :class="giaMio ? 'ncl__banner--info' : 'ncl__banner--attn'"
          >
            <span
              class="ncl__bannerIcon"
              aria-hidden="true"
            >!</span>
            <div class="ncl__bannerBody">
              <template v-if="giaMio">
                <strong>{{ nomeTrovato }}</strong> è già tra i tuoi clienti, con questa email.
                <div class="ncl__bannerActions">
                  <el-button
                    size="small"
                    type="primary"
                    @click="apriEsistente"
                  >
                    {{ ritornoPrenotazione ? 'Torna alla prenotazione' : 'Apri la scheda' }}
                  </el-button>
                  <el-button
                    size="small"
                    @click="cambiaEmail"
                  >
                    Usa un'altra email
                  </el-button>
                </div>
              </template>
              <template v-else>
                Questa email è già registrata in Simba: <strong>{{ nomeTrovato }}</strong>.
                Non va creato di nuovo: aggiungilo ai tuoi clienti{{ ritornoPrenotazione
                  ? ' e torna alla prenotazione'
                  : ', poi auto e valet si gestiscono dalla sua scheda' }}.
                <div class="ncl__bannerActions">
                  <el-button
                    size="small"
                    type="primary"
                    :loading="email.aggiungo"
                    @click="aggiungiEsistente"
                  >
                    Aggiungi ai miei clienti
                  </el-button>
                  <el-button
                    size="small"
                    @click="cambiaEmail"
                  >
                    Usa un'altra email
                  </el-button>
                </div>
              </template>
            </div>
          </div>

          <div
            class="ncl__grid"
            :class="{ 'is-dim': emailBloccante }"
          >
            <template v-if="isAzienda">
              <div class="ncl__f ncl__f--full">
                <label
                  class="campo-label"
                  for="ncl-ragione"
                >Ragione sociale <span class="ncl__req">*</span></label>
                <el-input
                  id="ncl-ragione"
                  v-model="cliente.company_name"
                  placeholder="es. Bianchi Noleggi S.r.l."
                />
              </div>
              <div class="ncl__f">
                <label
                  class="campo-label"
                  for="ncl-piva"
                >Partita IVA</label>
                <el-input
                  id="ncl-piva"
                  v-model="cliente.piva"
                />
              </div>
              <div class="ncl__f">
                <label
                  class="campo-label"
                  for="ncl-sdi"
                >Codice SDI</label>
                <el-input
                  id="ncl-sdi"
                  v-model="cliente.SDI"
                />
              </div>
              <div class="ncl__f">
                <label
                  class="campo-label"
                  for="ncl-pec"
                >PEC</label>
                <el-input
                  id="ncl-pec"
                  v-model="cliente.PEC"
                />
              </div>
            </template>

            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-nome"
              >{{ isAzienda ? 'Nome referente' : 'Nome' }} <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-nome"
                v-model="cliente.name"
              />
            </div>
            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-cognome"
              >{{ isAzienda ? 'Cognome referente' : 'Cognome' }} <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-cognome"
                v-model="cliente.surname"
              />
            </div>
            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-telefono"
              >Telefono <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-telefono"
                v-model="cliente.telephone_number"
                autocomplete="off"
              />
            </div>
            <div
              v-if="!isAzienda"
              class="ncl__f"
            >
              <label
                class="campo-label"
                for="ncl-nascita"
              >Data di nascita</label>
              <el-date-picker
                id="ncl-nascita"
                v-model="cliente.date_birth"
                type="date"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                placeholder="Facoltativa"
                class="ncl__date"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- ---------- indirizzo ---------- -->
      <section
        class="form-card"
        :class="{ 'is-dim': emailBloccante }"
      >
        <div class="form-card__head">
          <h3>Indirizzo</h3>
          <span class="form-card__spacer" />
        </div>
        <div class="ncl__body">
          <div class="ncl__grid">
            <div class="ncl__f ncl__f--2">
              <label
                class="campo-label"
                for="ncl-via"
              >Via / Piazza <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-via"
                v-model="cliente.address"
                @blur="verificaIndirizzo()"
              />
            </div>
            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-civico"
              >Civico <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-civico"
                v-model="cliente.street_number"
                @blur="verificaIndirizzo()"
              />
            </div>
            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-cap"
              >CAP <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-cap"
                v-model="cliente.cap"
                maxlength="5"
                @blur="verificaIndirizzo()"
              />
            </div>
            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-citta"
              >Città <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-citta"
                v-model="cliente.city"
                @blur="verificaIndirizzo()"
              />
            </div>
            <div class="ncl__f">
              <label
                class="campo-label"
                for="ncl-provincia"
              >Provincia <span class="ncl__req">*</span></label>
              <el-input
                id="ncl-provincia"
                v-model="cliente.province"
                maxlength="2"
                placeholder="es. BG"
                @blur="onProvinciaBlur"
              />
            </div>
          </div>

          <span
            v-if="geo.stato === 'verifica'"
            class="ncl__help"
          >Verifica dell'indirizzo in corso…</span>
          <div
            v-else-if="geo.stato === 'ok'"
            class="ncl__banner ncl__banner--ok"
          >
            <span
              class="ncl__bannerIcon"
              aria-hidden="true"
            >✓</span>
            <div class="ncl__bannerBody">
              Indirizzo trovato: <strong>{{ geo.label }}</strong>
            </div>
          </div>
          <div
            v-else-if="geo.stato === 'ko'"
            class="ncl__banner ncl__banner--danger"
            role="alert"
          >
            <span
              class="ncl__bannerIcon"
              aria-hidden="true"
            >!</span>
            <div class="ncl__bannerBody">
              <strong>Indirizzo non trovato.</strong> Controlla via, civico e CAP: finché non
              si trova, il cliente non può essere salvato.
              <div class="ncl__bannerActions">
                <el-button
                  size="small"
                  @click="verificaIndirizzo(true)"
                >
                  Riprova la verifica
                </el-button>
              </div>
            </div>
          </div>
          <span
            v-else-if="geo.stato === 'nd'"
            class="ncl__help"
          >Verifica non disponibile in questo momento: l'indirizzo sarà controllato al salvataggio.</span>
        </div>
      </section>

      <!-- ---------- auto ---------- -->
      <section
        class="form-card"
        :class="{ 'is-dim': emailBloccante }"
      >
        <div class="form-card__head">
          <h3>Auto</h3>
          <span class="ncl__count">{{ auto.length }}</span>
          <span class="form-card__spacer" />
          <span class="form-card__hint">Serve almeno un'auto per prenotare; puoi aggiungerla anche dopo.</span>
        </div>
        <div class="ncl__body">
          <p
            v-if="!auto.length"
            class="ncl__empty"
          >
            Nessuna auto: potrai aggiungerla dalla scheda del cliente.
          </p>

          <article
            v-for="(c, i) in auto"
            :key="c.chiave"
            class="ncl__car"
          >
            <div class="ncl__carHead">
              <span class="ncl__carTitle">Auto {{ i + 1 }}</span>
              <span class="ncl__spacer" />
              <el-button
                text
                size="small"
                class="ncl__remove"
                @click="rimuoviAuto(i)"
              >
                Rimuovi
              </el-button>
            </div>

            <div class="ncl__grid">
              <div class="ncl__f">
                <label
                  class="campo-label"
                  :for="`ncl-targa-${c.chiave}`"
                >Targa/Telaio <span class="ncl__req">*</span></label>
                <el-input
                  :id="`ncl-targa-${c.chiave}`"
                  v-model="c.targa"
                  class="ncl__plate"
                  maxlength="17"
                  placeholder="es. FA123ZZ o n° di telaio"
                  @blur="c.targa = c.targa.trim().toUpperCase()"
                />
              </div>
              <div class="ncl__f">
                <label
                  class="campo-label"
                  :for="`ncl-marca-${c.chiave}`"
                >Marca <span class="ncl__req">*</span></label>
                <el-input
                  :id="`ncl-marca-${c.chiave}`"
                  v-model="c.marca"
                  placeholder="es. BMW"
                />
              </div>
              <div class="ncl__f">
                <label
                  class="campo-label"
                  :for="`ncl-modello-${c.chiave}`"
                >Modello <span class="ncl__req">*</span></label>
                <el-input
                  :id="`ncl-modello-${c.chiave}`"
                  v-model="c.modello"
                  placeholder="es. Serie 3"
                />
              </div>
              <div class="ncl__f ncl__f--2">
                <label
                  class="campo-label"
                  :for="`ncl-intestatario-${c.chiave}`"
                >Intestatario</label>
                <el-input
                  :id="`ncl-intestatario-${c.chiave}`"
                  v-model="c.intestatario"
                  @input="(v) => (c.intestatarioToccato = v.trim() !== '')"
                />
                <span class="ncl__help">Uguale al cliente, finché non lo cambi</span>
              </div>
              <div class="ncl__f">
                <span class="campo-label">Cambio</span>
                <div
                  class="ncl__seg"
                  role="radiogroup"
                  :aria-label="`Cambio dell'auto ${i + 1}`"
                >
                  <button
                    v-for="o in CAMBI"
                    :key="o.value"
                    type="button"
                    role="radio"
                    :aria-checked="c.transmission === o.value"
                    :class="{ 'is-on': c.transmission === o.value }"
                    @click="c.transmission = o.value"
                  >
                    {{ o.label }}
                  </button>
                </div>
              </div>
            </div>

            <!-- pacchetto valet: appartiene a QUESTA auto. Lo propongono tutti i
                 ruoli del partner. -->
            <div class="ncl__valet">
              <div class="ncl__valetHead">
                <el-switch
                  v-model="c.valet.attivo"
                  :aria-label="`Proponi un pacchetto valet per l'auto ${i + 1}`"
                />
                <span class="ncl__valetTitle">Proponi un pacchetto valet</span>
                <span
                  v-if="!c.valet.attivo"
                  class="ncl__help ncl__help--inline"
                >Facoltativo</span>
              </div>

              <template v-if="c.valet.attivo">
                <!-- Prezzo dal listino: distanza casa del cliente →
                     sede, pickup all'anno e mesi. Niente più importi a mano. -->
                <PropostaValet
                  :indirizzo="cliente"
                  :sedi="contesto.sedi"
                  @change="(e) => (c.valet.esito = e)"
                />

                <p class="ncl__valetNote">
                  Il cliente paga il pacchetto; le corse che usano un valet si pagano solo per i
                  km oltre la fascia compresa. Al salvataggio la proposta arriva a noi di Simba:
                  saremo noi ad accettarla. Fino ad allora la trovi in
                  <strong>Clienti › Valet pickup da confermare</strong>.
                </p>
              </template>
            </div>
          </article>

          <el-button
            class="ncl__addCar"
            @click="aggiungiAuto"
          >
            + {{ auto.length ? "Aggiungi un'altra auto" : "Aggiungi un'auto" }}
          </el-button>
        </div>
      </section>

      <!-- ---------- azioni ---------- -->
      <div class="form-actions ncl__actions">
        <span class="ncl__sum">
          <strong>{{ isAzienda ? 'Azienda' : 'Privato' }}</strong> · {{ riepilogo }}
        </span>
        <span
          v-if="tentato && primoProblema"
          class="ncl__err"
          role="alert"
        >{{ primoProblema }}</span>
        <span
          v-else
          class="form-actions__hint"
        >I campi con * sono obbligatori</span>
        <span class="ncl__spacer" />
        <el-button @click="annulla">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="salvando"
          :disabled="emailBloccante || contesto.stato !== 'ok'"
          @click="salva"
        >
          {{ etichettaSalva }}
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, markRaw, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { OfficeBuilding, User } from '@element-plus/icons-vue';
import PropostaValet from '../Components/PropostaValet.vue';
import { isLoggedStore } from '../store/logged';
import { messageForError } from '../services/httpErrors';
import { abilitato, rottaCreazione } from '../services/servizi.js';
import {
  aggiungiAlPartner,
  contestoPartner,
  geolocalizza,
  proponiPacchetto,
  registraCliente,
  verificaEmail,
} from '../services/nuovoClienteAPI.js';

const TIPI = [
  { value: 'privato', nome: 'Privato', hint: 'Persona fisica', icona: markRaw(User) },
  { value: 'azienda', nome: 'Azienda', hint: 'Con ragione sociale e partita IVA', icona: markRaw(OfficeBuilding) },
];

const CAMBI = [
  { value: 'automatic', label: 'Automatico' },
  { value: 'manual', label: 'Manuale' },
];

const CAMPI_INDIRIZZO = ['address', 'street_number', 'cap', 'city', 'province'];
const EMAIL_RE = /^\S+@\S+\.\S+$/;

const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

// Chiave stabile per il v-for: l'indice cambierebbe togliendo un'auto in mezzo
// e i campi di un'auto finirebbero sotto a quella dopo.
let ultimaChiave = 0;

// L'intestatario nasce col nome del cliente: quasi sempre l'auto e' sua.
// `intestatarioToccato` ricorda se e' stato scritto a mano, per non
// sovrascriverlo quando si corregge il nome del cliente.
const autoVuota = (intestatario = '') => ({
  chiave: ++ultimaChiave,
  targa: '',
  marca: '',
  modello: '',
  intestatario,
  intestatarioToccato: false,
  transmission: 'automatic',
  // `esito` lo scrive PropostaValet: { problema, dati } per `proponiPacchetto`.
  valet: { attivo: false, esito: null },
});

const pulito = (v) => String(v ?? '').trim();

export default {
  name: 'AddCustomerEmployeePage',
  components: { PropostaValet },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const storeLogin = isLoggedStore();

    const sottotitolo = 'Anagrafica, indirizzo e auto. Su ogni auto puoi proporre un pacchetto valet.';

    // ---- da dove si arriva, dove si torna -----------------------------
    // I form di prenotazione mandano qui con `?ritorno=prenotazione&servizio=…`
    // (vedi `rottaNuovoCliente`): a salvataggio fatto si torna al form, che
    // ricarica i clienti e trova quello nuovo.
    const ritornoPrenotazione = computed(() => route.query.ritorno === 'prenotazione');

    const destinazione = computed(() => {
      if (!ritornoPrenotazione.value) return '/customer';
      const servizio = String(route.query.servizio || '');
      const data = String(route.query.data || '');
      const dataIso = /^\d{4}-\d{2}-\d{2}$/.test(data) ? data : '';
      return servizio && servizio !== 'journey' && abilitato(storeLogin, servizio)
        ? rottaCreazione(servizio, dataIso)
        : '/prenotazione/nuova';
    });

    // Tornando al form di Pickup & Delivery il cliente (e l'auto, se ne ha una
    // sola) arriva già scelto, come con «Prenota con quest'auto» della scheda
    // cliente. Il form Transfer non li legge: lì si torna com'era.
    const vaiAvanti = (scelto) => {
      let dest = destinazione.value;
      if (scelto?.id && String(route.query.servizio || '') === 'pickup' && dest.startsWith('/prenotazione/nuova')) {
        const url = new URL(dest, window.location.origin);
        url.searchParams.set('cliente', scelto.id);
        const auto = Array.isArray(scelto.cars) ? scelto.cars : [];
        if (auto.length === 1 && auto[0].id) url.searchParams.set('auto', auto[0].id);
        dest = `${url.pathname}${url.search}`;
      }
      router.push(dest);
    };
    const annulla = () => (window.history.state?.back ? router.back() : vaiAvanti());

    // ---- partner e sedi -----------------------------------------------
    const contesto = reactive({ stato: 'carico', partnerId: null, sedi: [] });

    const caricaContesto = async () => {
      contesto.stato = 'carico';
      try {
        const r = await contestoPartner(storeLogin.employee?.id || storeLogin.id);
        contesto.sedi = r.sedi;
        contesto.partnerId = storeLogin.partner?.id || r.partnerId;
        contesto.stato = contesto.partnerId ? 'ok' : 'errore';
      } catch (e) {
        console.warn('Partner non letto:', e?.response?.status);
        contesto.stato = 'errore';
      }
    };
    onMounted(caricaContesto);

    // ---- anagrafica ---------------------------------------------------
    const tipo = ref('privato');
    const isAzienda = computed(() => tipo.value === 'azienda');

    const cliente = reactive({
      mail: '',
      company_name: '',
      piva: '',
      SDI: '',
      PEC: '',
      name: '',
      surname: '',
      telephone_number: '',
      date_birth: null,
      address: '',
      street_number: '',
      cap: '',
      city: '',
      province: '',
    });

    const nomeCompleto = computed(
      () => (isAzienda.value && pulito(cliente.company_name)) || `${pulito(cliente.name)} ${pulito(cliente.surname)}`.trim(),
    );

    // ---- email --------------------------------------------------------
    // idle | verifica | libera | esiste | errore
    const email = reactive({ stato: 'idle', cliente: null, aggiungo: false });
    const campoEmail = ref(null);
    const mailPulita = computed(() => pulito(cliente.mail));

    // Qualunque modifica rimette la verifica da fare.
    watch(mailPulita, () => {
      email.stato = 'idle';
      email.cliente = null;
    });

    const controllaEmail = async () => {
      const mail = mailPulita.value;
      if (!EMAIL_RE.test(mail) || email.stato !== 'idle') return;
      email.stato = 'verifica';
      try {
        const { esiste, cliente: trovato } = await verificaEmail(mail);
        if (mail !== mailPulita.value) return; // risposta di un'email gia' cambiata
        email.cliente = esiste ? trovato : null;
        email.stato = esiste ? 'esiste' : 'libera';
      } catch (e) {
        if (mail === mailPulita.value) email.stato = 'errore';
      }
    };

    // Arrivando dal form di prenotazione con l'email già cercata, la si ritrova
    // scritta e verificata.
    if (typeof route.query.mail === 'string' && route.query.mail.trim()) {
      cliente.mail = route.query.mail.trim();
      onMounted(controllaEmail);
    }

    const emailBloccante = computed(() => email.stato === 'esiste');
    const giaMio = computed(() =>
      (email.cliente?.partners || []).some((p) => p.id === contesto.partnerId),
    );
    const nomeTrovato = computed(() => {
      const c = email.cliente || {};
      return pulito(c.company_name) || `${pulito(c.name)} ${pulito(c.surname)}`.trim() || c.mail;
    });

    const schedaCliente = (id) => router.push(`/details/customerAlive/${id}`);

    const apriEsistente = () =>
      ritornoPrenotazione.value ? vaiAvanti(email.cliente) : schedaCliente(email.cliente.id);

    const aggiungiEsistente = async () => {
      email.aggiungo = true;
      try {
        const c = await aggiungiAlPartner(email.cliente.mail);
        ElMessage({ type: 'success', message: `${nomeTrovato.value} è ora tra i tuoi clienti` });
        if (ritornoPrenotazione.value) vaiAvanti(c?.id ? c : email.cliente);
        else schedaCliente(c?.id || email.cliente.id);
      } catch (e) {
        console.error('add-partner:', e);
        ElMessage({ type: 'error', message: 'Non sono riuscito ad aggiungere il cliente. Riprova.' });
      } finally {
        email.aggiungo = false;
      }
    };

    const cambiaEmail = async () => {
      cliente.mail = '';
      await nextTick();
      campoEmail.value?.focus();
    };

    // ---- indirizzo ----------------------------------------------------
    // idle | verifica | ok | ko | nd (non disponibile)
    const geo = reactive({ stato: 'idle', label: '', chiave: '' });

    const indirizzoCompleto = computed(() => CAMPI_INDIRIZZO.every((k) => pulito(cliente[k])));
    const chiaveIndirizzo = computed(() =>
      CAMPI_INDIRIZZO.map((k) => pulito(cliente[k]).toLowerCase()).join('|'),
    );

    // L'esito vale per l'indirizzo verificato, non per quello di adesso.
    watch(chiaveIndirizzo, (k) => {
      if (k !== geo.chiave) {
        geo.stato = 'idle';
        geo.label = '';
      }
    });

    const verificaIndirizzo = async (forza = false) => {
      if (!indirizzoCompleto.value) return;
      const chiave = chiaveIndirizzo.value;
      if (!forza && chiave === geo.chiave && geo.stato !== 'idle') return;
      geo.chiave = chiave;
      geo.stato = 'verifica';
      try {
        const r = await geolocalizza({
          address: pulito(cliente.address),
          street_number: pulito(cliente.street_number),
          cap: pulito(cliente.cap),
          city: pulito(cliente.city),
          province: pulito(cliente.province),
        });
        if (chiave !== chiaveIndirizzo.value) return;
        geo.label = r?.label || '';
        geo.stato = r?.point ? 'ok' : 'ko';
      } catch (e) {
        if (chiave !== chiaveIndirizzo.value) return;
        // 500 = il geocoder non l'ha trovato (lo stesso che fallirebbe al
        // salvataggio); 503, rete e il resto non dicono niente sull'indirizzo.
        geo.stato = e?.response?.status === 500 ? 'ko' : 'nd';
      }
    };

    const onProvinciaBlur = () => {
      cliente.province = pulito(cliente.province).toUpperCase();
      verificaIndirizzo();
    };

    // ---- auto ---------------------------------------------------------
    const auto = ref([autoVuota()]);

    watch(nomeCompleto, (nome) => {
      auto.value.forEach((c) => {
        if (!c.intestatarioToccato) c.intestatario = nome;
      });
    });

    const aggiungiAuto = () => auto.value.push(autoVuota(nomeCompleto.value));
    const rimuoviAuto = (i) => auto.value.splice(i, 1);


    const euro = (v) => (v === null || v === undefined ? '—' : euroFmt.format(v));

    const normTarga = (t) => pulito(t).toUpperCase();

    const conValet = computed(() => auto.value.filter((c) => c.valet.attivo));

    // ---- controllo e riepilogo ----------------------------------------
    // Un messaggio alla volta, quello che blocca: come nell'admin.
    const primoProblema = computed(() => {
      if (emailBloccante.value) return 'Questa email è già registrata: usa il riquadro sopra';
      if (!mailPulita.value) return "Manca l'email";
      if (!EMAIL_RE.test(mailPulita.value)) return 'Email non valida';
      if (isAzienda.value && !pulito(cliente.company_name)) return 'Manca la ragione sociale';
      if (!pulito(cliente.name)) return isAzienda.value ? 'Manca il nome del referente' : 'Manca il nome';
      if (!pulito(cliente.surname)) return isAzienda.value ? 'Manca il cognome del referente' : 'Manca il cognome';
      if (!pulito(cliente.telephone_number)) return 'Manca il telefono';
      if (!indirizzoCompleto.value) return 'Indirizzo incompleto: servono via, civico, CAP, città e provincia';
      if (geo.stato === 'ko') return 'Indirizzo non trovato: controlla via, civico e CAP';
      for (let i = 0; i < auto.value.length; i++) {
        const c = auto.value[i];
        if (!pulito(c.targa) || !pulito(c.marca) || !pulito(c.modello)) {
          return `Auto ${i + 1}: mancano targa/telaio, marca o modello`;
        }
        if (auto.value.findIndex((x) => normTarga(x.targa) === normTarga(c.targa)) !== i) {
          return `Auto ${i + 1}: questa targa/telaio è già in un'altra auto`;
        }
        if (c.valet.attivo && (!c.valet.esito || c.valet.esito.problema)) {
          return `Auto ${i + 1}: ${c.valet.esito?.problema || 'prezzo del pacchetto valet non ancora calcolato'}`;
        }
      }
      if (contesto.stato !== 'ok') return 'Dati del partner non disponibili: riprova dal riquadro in alto';
      return '';
    });

    const riepilogo = computed(() => {
      const n = auto.value.length;
      const p = conValet.value.length;
      const parti = [n ? `${n} auto` : 'nessuna auto'];
      if (p) parti.push(p === 1 ? '1 pacchetto valet da proporre' : `${p} pacchetti valet da proporre`);
      return parti.join(' · ');
    });

    const etichettaSalva = computed(() => {
      const p = conValet.value.length;
      if (!p) return 'Crea cliente';
      return p === 1 ? 'Crea cliente e invia la proposta' : 'Crea cliente e invia le proposte';
    });

    // ---- salvataggio --------------------------------------------------
    const tentato = ref(false);
    const salvando = ref(false);

    const payloadCliente = () => ({
      role: 'customer',
      mail: mailPulita.value,
      name: pulito(cliente.name),
      surname: pulito(cliente.surname),
      telephone_number: pulito(cliente.telephone_number),
      date_birth: !isAzienda.value && cliente.date_birth ? cliente.date_birth : null,
      company_name: isAzienda.value ? pulito(cliente.company_name) : null,
      piva: isAzienda.value ? pulito(cliente.piva) || null : null,
      SDI: isAzienda.value ? pulito(cliente.SDI) || null : null,
      PEC: isAzienda.value ? pulito(cliente.PEC) || null : null,
      address: pulito(cliente.address),
      street_number: pulito(cliente.street_number),
      cap: pulito(cliente.cap),
      city: pulito(cliente.city),
      province: pulito(cliente.province).toUpperCase(),
      // Solo gli id: `partners` e' in cascade, e un oggetto partner intero
      // riscriverebbe le sue colonne.
      partners: [{ id: contesto.partnerId }],
      employees: [{ id: storeLogin.employee?.id || storeLogin.id }],
      cars: auto.value.map((c) => ({
        marca: pulito(c.marca),
        modello: pulito(c.modello),
        targa: normTarga(c.targa),
        intestatario: pulito(c.intestatario) || nomeCompleto.value,
        transmission: c.transmission,
        headquarters: contesto.sedi.map((h) => ({ id: h.id })),
      })),
    });

    // Pacchetti uno per auto: un fallimento non ferma gli altri, e il cliente
    // resta comunque creato. Si raccoglie tutto e lo si dice alla fine.
    // Al cliente non parte nessuna email: la proposta arriva a Simba, che la
    // accetta.
    const proponiPacchetti = async (creato) => {
      const esito = { proposti: 0, nonCreati: [] };
      for (const c of conValet.value) {
        const targa = normTarga(c.targa);
        const salvata = (creato?.cars || []).find((x) => normTarga(x.targa) === targa);
        if (!salvata?.id) {
          esito.nonCreati.push(targa);
          continue;
        }
        try {
          await proponiPacchetto({
            carId: salvata.id,
            partnerId: contesto.partnerId,
            ...c.valet.esito.dati,
          });
          esito.proposti += 1;
        } catch (e) {
          console.error('Pacchetto non creato:', targa, e);
          esito.nonCreati.push(targa);
        }
      }
      return esito;
    };

    const salva = async () => {
      tentato.value = true;
      if (primoProblema.value) {
        ElMessage({ type: 'warning', message: primoProblema.value });
        return;
      }

      salvando.value = true;
      try {
        let creato;
        try {
          creato = await registraCliente(payloadCliente());
        } catch (e) {
          const status = e?.response?.status;
          if (status === 409) {
            // Qualcuno l'ha registrata nel frattempo: si mostra chi e'.
            email.stato = 'idle';
            await controllaEmail();
            ElMessage({ type: 'error', message: 'Questa email è già registrata in Simba.' });
          } else if (status === 500) {
            ElMessage({
              type: 'error',
              duration: 8000,
              showClose: true,
              message:
                "Cliente non salvato. La causa più comune è un indirizzo che non si trova: controlla via, civico e CAP e riprova.",
            });
          } else {
            ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
          }
          return;
        }

        const esito = conValet.value.length
          ? await proponiPacchetti(creato)
          : { proposti: 0, nonCreati: [] };

        ElMessage({
          type: 'success',
          message: esito.proposti
            ? `Cliente creato. ${esito.proposti === 1 ? 'Il pacchetto valet è stato proposto' : `${esito.proposti} pacchetti valet sono stati proposti`}: arriva a noi di Simba e saremo noi ad accettarlo.`
            : 'Cliente creato.',
        });
        if (esito.nonCreati.length) {
          ElMessage({
            type: 'warning',
            duration: 10000,
            showClose: true,
            message: `Il pacchetto valet per ${esito.nonCreati.join(', ')} non è stato creato: il cliente e l'auto invece sì.`,
          });
        }

        vaiAvanti(creato);
      } finally {
        salvando.value = false;
      }
    };

    return {
      TIPI,
      CAMBI,
      sottotitolo,
      ritornoPrenotazione,
      annulla,
      contesto,
      caricaContesto,
      tipo,
      isAzienda,
      cliente,
      email,
      campoEmail,
      controllaEmail,
      emailBloccante,
      giaMio,
      nomeTrovato,
      apriEsistente,
      aggiungiEsistente,
      cambiaEmail,
      geo,
      verificaIndirizzo,
      onProvinciaBlur,
      auto,
      aggiungiAuto,
      rimuoviAuto,
      euro,
      primoProblema,
      riepilogo,
      etichettaSalva,
      tentato,
      salvando,
      salva,
    };
  },
};
</script>

<style scoped>
.ncl {
  max-width: 1080px;
  padding: 4px 8px 32px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ---- intestazione, come Clienti ---- */
.ncl__head {
  display: flex;
  align-items: center;
  gap: 12px;
}
.ncl__back {
  width: 34px;
  height: 34px;
  flex: none;
  display: grid;
  place-items: center;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.ncl__back:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
.ncl__title {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--color-text-primary);
}
.ncl__sub {
  margin: 3px 0 0;
  font-size: 13px;
  color: var(--color-text-secondary);
}

/* `.fx` porta le etichette sopra ai campi; la distanza dal bordo qui la da' la pagina. */
.ncl__stack {
  padding: 0;
}

.ncl__body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-card.is-dim,
.ncl__grid.is-dim {
  opacity: 0.45;
  pointer-events: none;
}

/* ---- tipo ---- */
.ncl__types {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.ncl__type {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  font: inherit;
  cursor: pointer;
  border: 1.5px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-primary);
  color: var(--color-text-primary);
}
.ncl__type:hover {
  border-color: var(--color-primary);
}
.ncl__type.is-on {
  border-color: var(--color-primary);
  background: #eeebfe;
  box-shadow: inset 0 0 0 1px var(--color-primary);
}
.ncl__typeIcon {
  width: 36px;
  height: 36px;
  flex: none;
  display: grid;
  place-items: center;
  border-radius: 7px;
  background: var(--color-bg-tertiary);
  color: #232c51;
}
.ncl__type.is-on .ncl__typeIcon {
  background: var(--color-bg-primary);
  color: var(--color-primary);
}
.ncl__typeName {
  display: block;
  font-size: 13.5px;
  font-weight: 700;
}
.ncl__typeHint {
  display: block;
  font-size: 12px;
  color: var(--color-text-secondary);
}

/* ---- campi ---- */
.ncl__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px 16px;
}
.ncl__f {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ncl__f--2 {
  grid-column: span 2;
}
.ncl__f--full {
  grid-column: 1 / -1;
}
.ncl__req {
  color: var(--state-danger);
}
.ncl__f :deep(.el-input-number),
.ncl__date,
.ncl__f :deep(.el-date-editor.el-input) {
  width: 100%;
}
.ncl__plate :deep(.el-input__inner) {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}
.ncl__help {
  margin-top: 5px;
  font-size: 11.5px;
  color: var(--color-text-secondary);
}
.ncl__help--ok {
  color: var(--state-ok);
  font-weight: 600;
}
.ncl__help--inline {
  margin-top: 0;
}

/* ---- cambio: due scelte, un solo valore ---- */
.ncl__seg {
  display: inline-flex;
  align-self: flex-start;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}
.ncl__seg button {
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  height: 30px;
  padding: 0 14px;
  border: 0;
  background: var(--color-bg-primary);
  color: var(--color-text-secondary);
  cursor: pointer;
}
.ncl__seg button + button {
  border-left: 1px solid #dcdfe6;
}
.ncl__seg button.is-on {
  background: var(--color-primary);
  color: #fff;
}

.ncl__type:focus-visible,
.ncl__seg button:focus-visible,
.ncl__back:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* ---- avvisi ---- */
.ncl__banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 13px;
  border: 1px solid;
  border-radius: 6px;
  font-size: 12.5px;
  line-height: 1.5;
}
.ncl__bannerIcon {
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
}
.ncl__bannerBody {
  flex: 1 1 auto;
  min-width: 0;
}
.ncl__bannerBody strong {
  font-weight: 650;
}
.ncl__bannerActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 9px;
}
.ncl__bannerActions .el-button + .el-button {
  margin-left: 0;
}
.ncl__banner--ok {
  color: var(--state-ok);
  background: var(--state-ok-bg);
  border-color: var(--state-ok-line);
}
.ncl__banner--ok .ncl__bannerIcon {
  background: var(--state-ok);
}
.ncl__banner--attn {
  color: #7c2d0a;
  background: var(--state-attn-bg);
  border-color: var(--state-attn-line);
}
.ncl__banner--attn .ncl__bannerIcon {
  background: var(--state-attn);
}
.ncl__banner--danger {
  color: #6e1a15;
  background: var(--state-danger-bg);
  border-color: var(--state-danger-line);
}
.ncl__banner--danger .ncl__bannerIcon {
  background: var(--state-danger);
}
.ncl__banner--info {
  color: #0f4a7a;
  background: var(--state-active-bg);
  border-color: var(--state-active-line);
}
.ncl__banner--info .ncl__bannerIcon {
  background: var(--state-active);
}

/* ---- auto ---- */
.ncl__count {
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-secondary);
  background: var(--color-bg-tertiary);
  border-radius: 999px;
  padding: 1px 8px;
}
.ncl__empty {
  margin: 0;
  font-size: 12.5px;
  color: var(--color-text-secondary);
}
.ncl__car {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg-secondary);
}
.ncl__carHead {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ncl__carTitle {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
}
.ncl__spacer {
  flex: 1 1 auto;
}
.ncl__remove {
  color: var(--state-danger);
}
.ncl__addCar {
  align-self: flex-start;
}

/* ---- pacchetto valet, dentro l'auto ---- */
.ncl__valet {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 13px;
  border-top: 1px dashed #dcdfe6;
}
.ncl__valetHead {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.ncl__valetTitle {
  font-size: 13px;
  font-weight: 650;
  color: var(--color-text-primary);
}
.ncl__valet .fx-stats {
  margin-top: 0;
}
.ncl__valetNote {
  margin: 0;
  max-width: 78ch;
  font-size: 12px;
  line-height: 1.5;
  color: var(--color-text-secondary);
}

/* ---- azioni: sempre a portata di mano ---- */
.ncl__actions {
  position: sticky;
  bottom: 12px;
  z-index: 5;
  box-shadow: 0 -4px 16px rgba(24, 32, 64, 0.06);
}
.ncl__actions .el-button + .el-button {
  margin-left: 0;
}
.ncl__sum {
  font-size: 12.5px;
  color: var(--color-text-secondary);
}
.ncl__sum strong {
  font-weight: 650;
  color: var(--color-text-primary);
}
.ncl__err {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--state-attn);
}

@media (max-width: 900px) {
  .ncl__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 560px) {
  .ncl__grid,
  .ncl__types {
    grid-template-columns: 1fr;
  }
  .ncl__f--2 {
    grid-column: 1 / -1;
  }
}
</style>
